import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAdminOrganizations, useSuspendOrg, useUnsuspendOrg } from '@/hooks/useOrganizations'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ConfirmDialog } from '@/components/admin/ConfirmDialog'
import { AdminOrganization } from '@/types'
import { Building2, Users, Search, Eye } from 'lucide-react'

export const OrganizationsListPage = () => {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [suspendTarget, setSuspendTarget] = useState<AdminOrganization | null>(null)
  const [unsuspendTarget, setUnsuspendTarget] = useState<AdminOrganization | null>(null)

  const { data, isLoading } = useAdminOrganizations(debouncedSearch)
  const suspendOrg = useSuspendOrg()
  const unsuspendOrg = useUnsuspendOrg()
  const navigate = useNavigate()

  const handleSearchChange = (value: string) => {
    setSearch(value)
    clearTimeout((handleSearchChange as { _t?: ReturnType<typeof setTimeout> })._t)
    const t = setTimeout(() => setDebouncedSearch(value), 400)
    ;(handleSearchChange as { _t?: ReturnType<typeof setTimeout> })._t = t
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Organizaciones</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    )
  }

  const orgs = data?.items ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Organizaciones</h1>
        <span className="text-sm text-muted-foreground">{data?.total ?? 0} total</span>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Buscar por nombre u owner..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      {orgs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
          <Building2 className="mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-lg font-medium text-muted-foreground">
            No hay organizaciones registradas
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {orgs.map((org) => (
            <Card key={org.id} className={org.is_suspended ? 'opacity-60' : ''}>
              <CardHeader className="flex flex-row items-start justify-between pb-2">
                <div className="min-w-0 flex-1">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="h-4 w-4 shrink-0" />
                    <span className="truncate">{org.name}</span>
                  </CardTitle>
                  <p className="mt-1 truncate text-xs text-muted-foreground">{org.owner_email}</p>
                </div>
                <Badge variant={org.is_suspended ? 'error' : 'default'}>
                  {org.is_suspended ? 'Suspendida' : 'Activa'}
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Owner: <span className="font-medium text-foreground">{org.owner_name}</span>
                </p>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{org.member_count} miembros</span>
                  </div>
                  <div className="text-muted-foreground">{org.managed_user_count} gestionados</div>
                </div>
                <div className="flex items-center justify-between border-t pt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate({ to: '/organizations/$orgId', params: { orgId: org.id } })
                    }
                  >
                    <Eye className="mr-1 h-3.5 w-3.5" />
                    Ver detalle
                  </Button>
                  {org.is_suspended ? (
                    <Button variant="outline" size="sm" onClick={() => setUnsuspendTarget(org)}>
                      Reactivar
                    </Button>
                  ) : (
                    <Button variant="destructive" size="sm" onClick={() => setSuspendTarget(org)}>
                      Suspender
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!suspendTarget}
        onOpenChange={(open) => !open && setSuspendTarget(null)}
        title="Suspender Organización"
        description={`¿Estás seguro de suspender "${suspendTarget?.name}"? Los usuarios no podrán acceder.`}
        confirmLabel="Suspender"
        onConfirm={async () => {
          if (suspendTarget) await suspendOrg.mutateAsync(suspendTarget.id)
        }}
        isLoading={suspendOrg.isPending}
      />

      <ConfirmDialog
        open={!!unsuspendTarget}
        onOpenChange={(open) => !open && setUnsuspendTarget(null)}
        title="Reactivar Organización"
        description={`¿Estás seguro de reactivar "${unsuspendTarget?.name}"?`}
        confirmLabel="Reactivar"
        onConfirm={async () => {
          if (unsuspendTarget) await unsuspendOrg.mutateAsync(unsuspendTarget.id)
        }}
        isLoading={unsuspendOrg.isPending}
      />
    </div>
  )
}
